import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { Label, Icon } from 'office-ui-fabric-react';
import ReactTable, { CellInfo, Column } from 'react-table';

import { ApiUrls, Permissions } from '@app/AppConstants';
import { ContractType, ContractTypeModel, PersonContractViewModel } from '@app/Models';

import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import { securityService } from '@app/Services/SecurityService';

import { InlineDateCell } from '@app/Components/CellFormatters/InlineDateCell';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { modalService, IModalDialogContent, ModalButtonType, ModalWindow, ModalDialogOptions } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';
import { NewContractModelDialog } from './NewContractModelDialog';
import { InlineContractDropdownCell, InlineContractTypeModelDropDownCell } from '@app/Components/CellFormatters';
import { ReasonCell } from '../ReasonCell';
import { IEntryExitBlockStore } from '../EntryExitBlockStore';
import { VariableDayTzvBlock } from '../VariableDayTzvBlock';
import { isCockpitAt } from '../Helpers/personFunctionHelper';

export type ContractEmploymentModelsDialogProps = {
    store: IEntryExitBlockStore;
    selectedContractId: string;
    updateContract: (contract: PersonContractViewModel) => Promise<boolean>;
    refreshData: () => void;
};

@observer
export class ContractEmploymentModelsDialog extends React.Component<ContractEmploymentModelsDialogProps> implements IModalDialogContent<void> {
    @observable private _loader: PromiseCompletion = new PromiseCompletion();
    private _store: IEntryExitBlockStore;

    constructor(props: ContractEmploymentModelsDialogProps) {
        super(props);
        this._store = props.store;
    }

    private get selectedContract(): PersonContractViewModel {
        return this._store.findSelectedContract(this.props.selectedContractId)!;
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        return {
            title: 'Contract employment models',
            width: '800px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }],
            loader: this._loader
        };
    }

    render() {
        const selectedContract = this.selectedContract;
        let headerDateRange = selectedContract.dateFrom ? '( ' + DateTimeService.toUiDate(selectedContract.dateFrom) + ' / ' : '( - / ';
        headerDateRange += selectedContract.dateTo ? DateTimeService.toUiDate(selectedContract.dateTo) + ' )' : '- )';

        if (!selectedContract.dateFrom && !selectedContract.dateTo) {
            headerDateRange = '';
        }
        const { hasPermission, permissionCss } = securityService;
        const cls = permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart);

        return (
            <>
                <div className="table-label">
                    <Label>
                        <span className="license-headline">
                            For '{selectedContract.type}' {headerDateRange}
                        </span>
                    </Label>
                    {hasPermission(Permissions.EditObjectPersonContractTZVPart, !AzureAuth.isReadOnly) && (
                        <button
                            className={'table-label-button ' + cls}
                            onClick={this._addContract}
                        >
                            <Icon iconName="Add" className="ms-IconTable" />
                        </button>
                    )}
                </div>
                <ReactTable<PersonContractViewModel>
                    data={this._contractEmploymentModels}
                    columns={this._contractModelsColumns}
                    defaultPageSize={10}
                    showPageSizeOptions={false}
                    resizable={true}
                    showPagination={false}
                    className={'ReactTable-fixed-height-modal'}
                />
            </>
        );
    }

    @computed
    private get _contractEmploymentModels() {
        const selectedContract = this.selectedContract;
        return this._store.personContracts
            .filter(contract => contract.externalId === selectedContract.externalId)
            .sort((a, b) => b.dateFrom.getTime() - a.dateFrom.getTime());
    }

    @action.bound
    private async _addContract() {
        const { store } = this.props;
        const selectedContract = this.selectedContract;
        const { button } = await modalService.show(NewContractModelDialog, {
            personId: store.personId,
            personFunctions: store.personFunctions,
            selectedContract: selectedContract
        });

        if (button === ModalButtonType.Save) {
            void this.props.refreshData();
        }
    }

    private _renderContractFromDate = (cellInfo: CellInfo) => {
        const selectedContract = this.selectedContract;
        const isSelectedContract = cellInfo.original.id === selectedContract.id;
        const minDate = DateTimeService.addDays(selectedContract.dateFrom, 1);
        return <InlineDateCell<PersonContractViewModel>
            value={cellInfo.value}
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonContractViewModel}
            disabled={cellInfo.row._original.isFinalized}
            isReadOnly={isSelectedContract || cellInfo.row._original.isFinalized || !securityService.hasPermission(Permissions.EditContractDateFrom, !AzureAuth.isReadOnly)}
            minDate={minDate}
            onValidate={InlineDateValidationService.validateDate}
            onSelect={this._onContractTableColumnChange} />;
    };

    private _renderContractToDate = (cellInfo: CellInfo) => {
        const selectedContract = this.selectedContract;
        const minDate = DateTimeService.addDays(selectedContract.dateFrom, 1);
        return <InlineDateCell<PersonContractViewModel>
            value={cellInfo.value}
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PersonContractViewModel}
            isReadOnly
            canBeEmpty
            minDate={minDate}
            onValidate={InlineDateValidationService.validateDate}
            onSelect={this._onContractTableColumnChange}
            onConfirmation={this._onConfirmContractDateTo} />;
    };

    @action.bound
    private _onConfirmContractDateTo(row: PersonContractViewModel) {
        return modalService.showConfirmation(`Do you really want to set contract end to '${(row.dateTo ? DateTimeService.toUiDate(row.dateTo) : '<empty>')}'?`);
    }

    private _renderContractEmploymentSubType = (cellInfo: CellInfo) => {
        const contract = cellInfo.row._original as PersonContractViewModel;
        let canUpdateTypeModel = (!contract.isFinalized || contract.isFinalized && contract.typeModel === 'None') && securityService.hasPermission(Permissions.EditContractTypeModels, !AzureAuth.isReadOnly);
        if (!securityService.hasPermission(Permissions.EditObjectPersonContractTZVPart, !AzureAuth.isReadOnly) && contract.type === ContractType.Tzv) {
            canUpdateTypeModel = false;
        }
        return (
            <>
                <InlineContractTypeModelDropDownCell
                    typeModel={cellInfo.value}
                    onChangeHandler={this._onContractTableColumnChange}
                    row={cellInfo.row._original}
                    fieldKey="typeModel"
                    isReadOnly={!canUpdateTypeModel}
                    personFunctions={this._store.personFunctions}
                />
                {contract.combinationVariableDayBlockLength && contract.combinationVariableDaySplitType && (
                    <div><small>combined with</small> {ContractTypeModel.TZV_VariableDay}</div>
                )}
            </>
        );
    };

    private _onContractTableColumnChange = async (row: PersonContractViewModel, initialModel?: ContractTypeModel) => {
        const contract = row;
        const updatingFinalizedNone = contract.isFinalized && initialModel === 'None';
        if (updatingFinalizedNone && !await modalService.showConfirmation(`Do you really want to change contract ${DateTimeService.toUiDate(contract.dateFrom)} - ${contract.dateTo ? DateTimeService.toUiDate(contract.dateTo) : '~'} to type ${row.typeModel}. Once it's changed, you can't update model for this contract. Only new contract might be created.`, 'WARNING - Contract Model Change', {
            color: 'danger',
            confirmButtonTitle: 'Change (no undo possible)'
        })){
            return false;
        }

        return await this.props.updateContract(row);
    };

    private _renderContractModelDetails = (cellInfo: CellInfo) => {
        const detailsElements = InlineContractDropdownCell.renderPartTimeInfo(cellInfo.row._original);
        if (!detailsElements.length) return <React.Fragment />;

        return detailsElements.map((el, i) => <React.Fragment key={i}>{el}</React.Fragment>);
    };

    private _renderContractCommentCell = (cellInfo: CellInfo) => {
        return <ReasonCell
            key={cellInfo.row.id}
            value={cellInfo.value || ''}
            id={cellInfo.row._original.id}
            onChange={newValue => this._onCommentCellChange(cellInfo.row._original, newValue)}
            readonlyMode={!securityService.hasPermission(Permissions.EditContractComment, !AzureAuth.isReadOnly)}
        />;
    };

    @action.bound
    private async _onCommentCellChange(row: PersonContractViewModel, newValue: string) {
        const contract = { ...row } as PersonContractViewModel;
        const oldValue = contract.comment;
        contract.comment = newValue;

        const result = await this.props.updateContract(contract);
        if (!result) {
            contract.comment = oldValue;
            return false;
        }

        return true;
    }

    private _renderContractActionsColumn = (cellInfo: CellInfo) => {
        const row = cellInfo.original as PersonContractViewModel;
        const isDeletable = DateTimeService.toJSONDateFormat(row.dateFrom) !== row.externalId;
        const isCockpit = isCockpitAt(this._store.personFunctions, row.dateFrom);
        const canAddVariableDayModel = isCockpit && row.type === ContractType.Tzv && row.typeModel !== ContractTypeModel.TZV_R;

        return (
            <>
                {canAddVariableDayModel && <Icon iconName="RedEye" className="ms-Icon-action mr-2" title={`Combine with ${ContractTypeModel.TZV_VariableDay}`} onClick={() => this._onAddVariableDayContractModel(cellInfo)} />}
                {isDeletable && <Icon iconName="Delete" className="ms-Icon-action" title="Delete model" onClick={() => this._onDeletePersonContract(cellInfo)} />}
            </>
        );
    };

    private async _onDeletePersonContract(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this contract model?', 'Warning', {
            onConfirm: async () => {
                await this._deleteContract(cellInfo.value);
            }
        });
    }

    @action.bound
    private async _deleteContract(id: string) {
        await ApiService.deleteData(ApiUrls.PersonContractUrl + '?personContractId=' + id, { completion: this._loader });
        NotificationHandler.showSuccess('Person Contract Model was deleted', 'Success message');
        void this.props.refreshData();
    }

    private async _onAddVariableDayContractModel(cellInfo: CellInfo) {
        const row = cellInfo.original as PersonContractViewModel;
        modalService.show(VariableDayTzvBlock, {
            contract: row,
            personFunctions: this._store.personFunctions,
            onSave: this.props.updateContract
        });
    }

    @computed
    private get _contractModelsColumns(): Column<PersonContractViewModel>[] {
        return [
            {
                Header: 'From',
                accessor: 'dateFrom',
                Cell: this._renderContractFromDate,
                maxWidth: 110,
                className: securityService.permissionCss(Permissions.ViewContractDateFrom, Permissions.EditContractDateFrom),
            },
            {
                Header: 'To',
                accessor: 'dateTo',
                Cell: this._renderContractToDate,
                maxWidth: 110,
                className: securityService.permissionCss(Permissions.ViewContractDateTo, Permissions.EditContractDateTo),
            },
            {
                Header: 'Models',
                accessor: 'typeModel',
                Cell: this._renderContractEmploymentSubType,
                maxWidth: 200,
                show: securityService.hasPermission(Permissions.ViewContractTypeModels),
                className: securityService.permissionCss(Permissions.ViewContractTypeModels, Permissions.EditContractTypeModels)
            },
            {
                Header: 'Model Details',
                accessor: 'modelDetails',
                Cell: this._renderContractModelDetails,
                maxWidth: 160,
                className: securityService.permissionCss(Permissions.ViewFunctionContractOverview),
                show: securityService.hasPermission(Permissions.ViewFunctionContractOverview)
            },
            {
                Header: 'Comment',
                accessor: 'comment',
                Cell: this._renderContractCommentCell,
                show: securityService.hasPermission(Permissions.ViewContractComment),
                className: securityService.permissionCss(Permissions.ViewContractComment, Permissions.EditContractComment)
            },
            {
                Header: '',
                accessor: 'id',
                sortable: false,
                Cell: this._renderContractActionsColumn,
                show: securityService.hasPermission(Permissions.EditObjectPersonContractTZVPart, !AzureAuth.isReadOnly),
                maxWidth: 60,
                className: securityService.permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)
            }
        ];
    }
}